// Otimizador de consultas para o sistema AI School
import { supabase } from '../lib/supabase';

/**
 * Configurações de timeout otimizadas
 */
export const QUERY_CONFIG = {
  // Timeouts reduzidos para melhor performance
  USER_TYPE_CHECK: 2000,      // 2 segundos (reduzido de 3s)
  PROFILE_LOAD: 1500,         // 1.5 segundos
  AUTH_VERIFICATION: 1000,    // 1 segundo
  CACHE_TIMEOUT: 500,         // 500ms para operações rápidas
  
  // Configurações de retry
  MAX_RETRIES: 2,
  RETRY_DELAY: 300,
  
  // Configurações de cache
  CACHE_DURATION: 5 * 60 * 1000, // 5 minutos
};

/**
 * Cache em memória para consultas frequentes
 */
const queryCache = new Map();

/**
 * Verifica se uma consulta está em cache e ainda é válida
 */
const isCached = (key) => {
  const cached = queryCache.get(key);
  if (!cached) return false;
  
  const now = Date.now();
  return (now - cached.timestamp) < QUERY_CONFIG.CACHE_DURATION;
};

/**
 * Adiciona resultado ao cache
 */
const addToCache = (key, data) => {
  queryCache.set(key, {
    data,
    timestamp: Date.now()
  });
};

/**
 * Limpa cache expirado
 */
const cleanExpiredCache = () => {
  const now = Date.now();
  for (const [key, value] of queryCache.entries()) {
    if ((now - value.timestamp) > QUERY_CONFIG.CACHE_DURATION) {
      queryCache.delete(key);
    }
  }
};

/**
 * Executa consulta com retry e cache
 */
export const executeQuery = async (queryFn, cacheKey = null, options = {}) => {
  const {
    timeout = QUERY_CONFIG.USER_TYPE_CHECK,
    useCache = true,
    maxRetries = QUERY_CONFIG.MAX_RETRIES
  } = options;

  // Verificar cache se habilitado
  if (useCache && cacheKey && isCached(cacheKey)) {
    console.log('📋 Usando resultado do cache:', cacheKey);
    return queryCache.get(cacheKey).data;
  }

  // Executar consulta com retry
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const result = await Promise.race([
        queryFn(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Query timeout')), timeout)
        )
      ]);

      // Adicionar ao cache se habilitado
      if (useCache && cacheKey) {
        addToCache(cacheKey, result);
      }

      return result;
      
    } catch (error) {
      if (attempt === maxRetries) {
        throw error;
      }
      
      console.log(`⚠️ Tentativa ${attempt + 1} falhou, tentando novamente...`);
      await new Promise(resolve => 
        setTimeout(resolve, QUERY_CONFIG.RETRY_DELAY * (attempt + 1))
      );
    }
  }
};

/**
 * Consulta otimizada para verificar tipo de usuário
 */
export const checkUserTypeOptimized = async (authUserId) => {
  const cacheKey = `user_type_${authUserId}`;
  
  console.log('🔍 [queryOptimizer] Verificando tipo de usuário para:', authUserId);
  
  return executeQuery(
    async () => {
      console.log('🔍 [queryOptimizer] Executando consulta paralela...');
      
      // Consulta paralela otimizada
      const [alunoResult, profileResult] = await Promise.allSettled([
        supabase
          .from('aluno_profiles')
          .select('id, role, aluno_id')
          .eq('id', authUserId)
          .single(),
        
        supabase
          .from('profiles')
          .select('id, role')
          .eq('id', authUserId)
          .single()
      ]);

      console.log('🔍 [queryOptimizer] Resultados das consultas:', {
        alunoResult: {
          status: alunoResult.status,
          data: alunoResult.value?.data,
          error: alunoResult.reason?.message
        },
        profileResult: {
          status: profileResult.status,
          data: profileResult.value?.data,
          error: profileResult.reason?.message
        }
      });

      // Analisar resultados
      if (alunoResult.status === 'fulfilled' && alunoResult.value.data) {
        const profile = alunoResult.value.data;
        console.log('✅ [queryOptimizer] Usuário encontrado em aluno_profiles:', profile);
        
        // Na base: 'student' = aluno, 'admin' = administrador
        if (profile.role === 'student') {
          console.log('🎓 [queryOptimizer] Tipo detectado: ALUNO (role: student)');
          return 'student'; // Retornar 'student' para manter consistência com a base
        } else {
          console.log('👨‍💼 [queryOptimizer] Tipo detectado: ADMIN (role: ' + profile.role + ')');
          return 'admin';
        }
      }

      if (profileResult.status === 'fulfilled' && profileResult.value.data) {
        const profile = profileResult.value.data;
        console.log('✅ [queryOptimizer] Usuário encontrado em profiles:', profile);
        
        // Na base: 'student' = aluno, 'admin' = administrador
        if (profile.role === 'student') {
          console.log('🎓 [queryOptimizer] Tipo detectado: ALUNO (role: student)');
          return 'student'; // Retornar 'student' para manter consistência com a base
        } else {
          console.log('👨‍💼 [queryOptimizer] Tipo detectado: ADMIN (role: ' + profile.role + ')');
          return 'admin';
        }
      }

      console.log('⚠️ [queryOptimizer] Usuário não encontrado nas tabelas, usando default: ADMIN');
      return 'admin'; // Default
    },
    cacheKey,
    { timeout: QUERY_CONFIG.USER_TYPE_CHECK, useCache: true }
  );
};

/**
 * Consulta otimizada para carregar perfil
 */
export const loadProfileOptimized = async (authUser, userType) => {
  const cacheKey = `profile_${authUser.id}_${userType}`;
  
  console.log('🔄 [queryOptimizer] Carregando perfil para:', {
    userId: authUser.id,
    userType: userType,
    cacheKey: cacheKey
  });
  
  return executeQuery(
    async () => {
      console.log('🔍 [queryOptimizer] Executando consulta de perfil...');
      
      if (userType === 'student') {
        console.log('🔍 [queryOptimizer] Buscando em aluno_profiles...');
        const { data, error } = await supabase
          .from('aluno_profiles')
          .select('*')
          .eq('id', authUser.id)
          .single();
          
        if (error) {
          console.warn('⚠️ [queryOptimizer] Erro ao buscar em aluno_profiles:', error.message);
        } else {
          console.log('✅ [queryOptimizer] Perfil encontrado em aluno_profiles:', data);
        }
        
        return data;
      } else {
        console.log('🔍 [queryOptimizer] Buscando em profiles...');
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', authUser.id)
          .single();
          
        if (error) {
          console.warn('⚠️ [queryOptimizer] Erro ao buscar em profiles:', error.message);
        } else {
          console.log('✅ [queryOptimizer] Perfil encontrado em profiles:', data);
        }
        
        return data;
      }
    },
    cacheKey,
    { timeout: QUERY_CONFIG.PROFILE_LOAD, useCache: true }
  );
};

/**
 * Limpa cache específico
 */
export const clearQueryCache = (pattern = null) => {
  if (pattern) {
    // Limpar cache específico
    for (const key of queryCache.keys()) {
      if (key.includes(pattern)) {
        queryCache.delete(key);
      }
    }
    console.log(`🗑️ Cache limpo para padrão: ${pattern}`);
  } else {
    // Limpar todo o cache
    queryCache.clear();
    console.log('🗑️ Todo o cache de consultas foi limpo');
  }
};

/**
 * Estatísticas do cache
 */
export const getCacheStats = () => {
  const now = Date.now();
  let validEntries = 0;
  let expiredEntries = 0;
  
  for (const [_, value] of queryCache.entries()) {
    if ((now - value.timestamp) < QUERY_CONFIG.CACHE_DURATION) {
      validEntries++;
    } else {
      expiredEntries++;
    }
  }
  
  return {
    total: queryCache.size,
    valid: validEntries,
    expired: expiredEntries,
    memoryUsage: `${(JSON.stringify(queryCache).length / 1024).toFixed(2)} KB`
  };
};

// Limpar cache expirado periodicamente
setInterval(cleanExpiredCache, QUERY_CONFIG.CACHE_DURATION);

export default {
  executeQuery,
  checkUserTypeOptimized,
  loadProfileOptimized,
  clearQueryCache,
  getCacheStats,
  QUERY_CONFIG
};
